import React, { useState, useEffect } from 'react';
import { Text, Card, Grid, Input, Button, Spacer } from '@geist-ui/core';
import { Search, Plus, Save, Trash2, Edit } from '@geist-ui/icons';
import './Vault.css';

const VaultView = () => {
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newItem, setNewItem] = useState({ name: '', description: '', type: 'item', source: '' });
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Load saved items from localStorage on initial render
  useEffect(() => {
    const savedItems = localStorage.getItem('vault_items');
    if (savedItems) {
      try {
        setItems(JSON.parse(savedItems));
      } catch (error) {
        console.error('Error loading vault items:', error);
      }
    }
  }, []);
  
  // Save items to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('vault_items', JSON.stringify(items));
  }, [items]);
  
  const handleAddItem = () => {
    if (newItem.name.trim() === '') return;
    
    const timestamp = new Date().toISOString();
    const itemToAdd = {
      ...newItem,
      id: `item_${timestamp}`,
      dateAdded: timestamp
    };
    
    setItems(prev => [...prev, itemToAdd]);
    setNewItem({ name: '', description: '', type: 'item', source: '' });
    setShowAddModal(false);
  };
  
  const handleDeleteItem = (id) => {
    setItems(prev => prev.filter(item => item.id !== id));
    if (selectedItem && selectedItem.id === id) {
      setSelectedItem(null);
    }
  };
  
  const handleEditItem = (item) => {
    setSelectedItem(item);
    setEditMode(true);
  };
  
  const handleUpdateItem = () => {
    setItems(prev => prev.map(item => 
      item.id === selectedItem.id ? selectedItem : item
    ));
    setEditMode(false);
    setSelectedItem(null);
  };
  
  const itemTypes = [
    { value: 'item', label: 'Item' },
    { value: 'note', label: 'Note' },
    { value: 'character', label: 'Character' },
    { value: 'location', label: 'Location' }
    // Artifact type removed
  ];
  
  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.source.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="vault-container">
      <div className="vault-header">
        <Text h1>Adventurer's Vault</Text>
        <Text p>Store and organize important items, notes, and discoveries from your adventures.</Text>
      </div>
      
      <div className="vault-search">
        <Input 
          icon={<Search />} 
          placeholder="Search your vault..." 
          width="100%" 
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      
      <div className="vault-content">
        <div className="items-grid">
          {/* Always display the filtered items if any exist */}
          {filteredItems.map(item => {
            const itemType = itemTypes.find(t => t.value === item.type) || itemTypes[0];
            
            return (
              <div key={item.id} className="card-outer-frame">
                <div className="card-body minimalist">
                  <div className="card-header-section">
                    <span className="card-name">{item.name}</span>
                  </div>
                  
                  <div className="card-image-area">
                    <img 
                      src={`https://placehold.co/340x210/f8f8f8/888888?text=${itemType.label}&font=crimsontext`}
                      alt={`${item.name} artwork`}
                    />
                  </div>
                  
                  <div className="card-type-bar">
                    <span>{itemType.label}</span>
                  </div>
                  
                  <div className="card-rules-text-box">
                    <p>{item.description}</p>
                  </div>
                  
                  {item.source && (
                    <div className="card-source">
                      <p>Source: {item.source}</p>
                    </div>
                  )}
                  
                  <div className="card-actions">
                    <Button 
                      auto 
                      scale={1/2} 
                      icon={<Edit />}
                      onClick={() => handleEditItem(item)}
                    ></Button>
                    <Button 
                      auto 
                      type="error" 
                      ghost 
                      scale={1/2} 
                      icon={<Trash2 />}
                      onClick={() => handleDeleteItem(item.id)}
                    ></Button>
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Always show the add card regardless of whether there are items */}
          <div className="card-outer-frame add-card" onClick={() => setShowAddModal(true)}>
            <div className="card-body minimalist add-card-body">
              <div className="add-icon">
                <Plus size={36} />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {showAddModal && (
        <div className="modal-overlay">
          <div className="edit-modal">
            <Text h3>Add New Entry</Text>
            <Input 
              placeholder="Name" 
              width="100%" 
              value={newItem.name}
              onChange={(e) => setNewItem({...newItem, name: e.target.value})}
            />
            <Spacer h={0.5} />
            <Input 
              placeholder="Source (book/chapter)" 
              width="100%" 
              value={newItem.source}
              onChange={(e) => setNewItem({...newItem, source: e.target.value})}
            />
            <Spacer h={0.5} />
            <select 
              className="vault-select"
              value={newItem.type}
              onChange={(e) => setNewItem({...newItem, type: e.target.value})}
            >
              {itemTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <Spacer h={0.5} />
            <textarea
              className="vault-textarea"
              placeholder="Description"
              rows={4}
              value={newItem.description}
              onChange={(e) => setNewItem({...newItem, description: e.target.value})}
            />
            <Spacer h={1} />
            <div className="modal-actions">
              <Button auto type="success" icon={<Plus />} onClick={handleAddItem}>
                Add to Vault
              </Button>
              <Spacer w={1} />
              <Button auto type="secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {editMode && selectedItem && (
        <div className="modal-overlay">
          <div className="edit-modal">
            <Text h3>Edit Entry</Text>
            <Input 
              placeholder="Name" 
              width="100%" 
              value={selectedItem.name}
              onChange={(e) => setSelectedItem({...selectedItem, name: e.target.value})}
            />
            <Spacer h={0.5} />
            <Input 
              placeholder="Source (book/chapter)" 
              width="100%" 
              value={selectedItem.source}
              onChange={(e) => setSelectedItem({...selectedItem, source: e.target.value})}
            />
            <Spacer h={0.5} />
            <select 
              className="vault-select"
              value={selectedItem.type}
              onChange={(e) => setSelectedItem({...selectedItem, type: e.target.value})}
            >
              {itemTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <Spacer h={0.5} />
            <textarea
              className="vault-textarea"
              placeholder="Description"
              rows={4}
              value={selectedItem.description}
              onChange={(e) => setSelectedItem({...selectedItem, description: e.target.value})}
            />
            <Spacer h={1} />
            <div className="modal-actions">
              <Button auto type="success" icon={<Save />} onClick={handleUpdateItem}>
                Save Changes
              </Button>
              <Spacer w={1} />
              <Button auto type="secondary" onClick={() => {
                setEditMode(false);
                setSelectedItem(null);
              }}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VaultView;